import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { useToast } from '@/components/ui/use-toast'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { isAxiosError } from '@/api/business'
import { createContractControl } from '@/api/business/contractcontrol'
import { ContractControlKeys } from '@/queries/useContractControlQuery'
import Button from '@/components/Button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage
} from '@/components/ui/form'
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
  } from "@/components/ui/popover"
import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod' 
import { format } from 'date-fns'
import { CalendarIcon } from 'lucide-react'
import { Calendar } from "@/components/ui/calendar"
import { cn } from "@/lib/utils"

interface AddDialogProps {
    isOpen: boolean
    onClose: () => void
}

const AddDialog = ({ isOpen, onClose }: AddDialogProps) => {
    const queryClient = useQueryClient()
    const { toast } = useToast() 

    const schema = z.object({
        NM_CONTRATO: z.string().max(200, {message: 'Maximo de 200 caracteres'}).min(1, {
            message: 'O campo Nome do contrato é obrigatório',
        }),
        SK_TEMPO_VENCIMENTO: z.date({
            required_error: "Data de Vencimento é Obrigatório",
        }),
        SK_TEMPO_INCIO_NOTIFICACAO: z.date({
            required_error: "Data de Inicio de Notificação de Vencimento é Obrigatório",
        }),
        DS_EMAILS_NOTIFICACAO: z.string().max(1000, {message: 'Maximo de 200 caracteres'}),
        DS_CONTRATO: z.string() 
    })

    const form = useForm<z.infer<typeof schema>>({
        resolver: zodResolver(schema),
        defaultValues: {
            NM_CONTRATO: '',
            SK_TEMPO_VENCIMENTO: new Date(),
            SK_TEMPO_INCIO_NOTIFICACAO: new Date(),
            DS_EMAILS_NOTIFICACAO: '',
            DS_CONTRATO: '', 
        },
    })

    const { mutate, isPending: mutateLoading } = useMutation({
        mutationFn: createContractControl,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ContractControlKeys.lists() })
            onClose()
            form.reset()
            toast({
                title: 'Contrato criado com sucesso',
            })
        },
        onError: (err) => {
            let errorMessage =
                'Não foi possível criar um contrato. Tente novamente mais tarde.'

            if (isAxiosError(err)) {
                errorMessage = err.response?.data.message || errorMessage
            }

            toast({
                title: 'Erro ao criar contrato',
                description: errorMessage,
                variant: 'destructive',
            })
        },
    })

    const onSubmit = (data: z.infer<typeof schema>) => {
        mutate({
            DS_CONTRATO: data.DS_CONTRATO,
            SK_TEMPO_VENCIMENTO: Number(data.SK_TEMPO_VENCIMENTO.toString().split('-').join('')),
            SK_TEMPO_INCIO_NOTIFICACAO: Number(data.SK_TEMPO_INCIO_NOTIFICACAO.toString().split('-').join('')),
            DS_EMAILS_NOTIFICACAO: data.DS_EMAILS_NOTIFICACAO,
            NM_CONTRATO: data.NM_CONTRATO
        })
    }

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className='max-w-[1000px] overflow-auto  '>
                <DialogHeader className='px-2'>
                    <DialogTitle>Adicionar Contrato</DialogTitle>
                </DialogHeader>
                <Form {...form}>
                    <form
                        onSubmit={form.handleSubmit(onSubmit)}
                        className="space-y-2 px-2 overflow-hidden overflow-y-auto"
                    >
                        <div className='grid gap-4'>
                            <FormField
                                name="NM_CONTRATO"
                                control={form.control}
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Nome do Contrato</FormLabel>
                                        <FormControl>
                                            <Input {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            /> 
                        </div> 
                        <div className='flex gap-4'>
                            <FormField
                                name="SK_TEMPO_VENCIMENTO"
                                control={form.control}
                                render={({ field }) => (
                                    <FormItem className="flex flex-col">
                                        <FormLabel>Data do Vencimento do Contrato</FormLabel>
                                        <Popover>
                                            <PopoverTrigger asChild>
                                            <FormControl>
                                                <Button
                                                variant={"outline"}
                                                className={cn(
                                                    "w-[240px] pl-3 text-left font-normal",
                                                    !field.value && "text-muted-foreground"
                                                )}
                                                >
                                                {field.value ? (
                                                    format(field.value, "PPP")
                                                ) : (
                                                    <span>Selecionar Data</span>
                                                )}
                                                <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                                                </Button>
                                            </FormControl>
                                            </PopoverTrigger>
                                            <PopoverContent className="w-auto p-0" align="start">
                                            <Calendar
                                                mode="single"
                                                selected={field.value}
                                                onSelect={field.onChange} 
                                                initialFocus
                                            />
                                            </PopoverContent>
                                        </Popover> 
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                name="SK_TEMPO_INCIO_NOTIFICACAO"
                                control={form.control}
                                render={({ field }) => (
                                    <FormItem className="flex flex-col">
                                    <FormLabel>Data para início da notificação de Vencimento</FormLabel>
                                    <Popover>
                                        <PopoverTrigger asChild>
                                        <FormControl>
                                            <Button
                                            variant={"outline"}
                                            className={cn(
                                                "w-[240px] pl-3 text-left font-normal",
                                                !field.value && "text-muted-foreground"
                                            )}
                                            >
                                            {field.value ? (
                                                format(field.value, "PPP")
                                            ) : (
                                                <span>Selecionar Data</span>
                                            )}
                                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                                            </Button>
                                        </FormControl>
                                        </PopoverTrigger>
                                        <PopoverContent className="w-auto p-0" align="start">
                                        <Calendar
                                            mode="single"
                                            selected={field.value}
                                            onSelect={field.onChange} 
                                            initialFocus
                                        />
                                        </PopoverContent>
                                    </Popover> 
                                    <FormMessage />
                                </FormItem>
                                )}
                            /> 
                        </div> 
                        <div className='grid  gap-4'> 
                            <FormField
                                name="DS_EMAILS_NOTIFICACAO"
                                control={form.control}
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>E-Mails para Notificação de Vencimento</FormLabel>
                                        <FormControl>
                                            <Textarea {...field} /> 
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>
                        <div className='grid  gap-4'> 
                            <FormField
                                name="DS_CONTRATO"
                                control={form.control}
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Descrição do Contrato</FormLabel>
                                        <FormControl>
                                            <Textarea {...field} /> 
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>
                        <DialogFooter>
                            <Button
                                type="button"
                                onClick={onClose}
                                variant="ghost"
                            >
                                Cancelar
                            </Button>
                            <Button type="submit" isLoading={mutateLoading}>
                                Confirmar
                            </Button>
                        </DialogFooter> 
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    )
}

export default AddDialog
